import React from "react";
import { Calculator, ListRestart, Loader2, X } from "lucide-react";
import Tooltip from "components/ui/Tooltip";
import Chip from "../../../components/Chip";
import { computeSummary } from "../features/source-field.utils";

const inputClass = "h-8 w-full rounded-md border border-border bg-surface px-2 text-xs text-text_primary outline-none focus:border-border_strong disabled:bg-surface_alt disabled:text-text_muted";

// Small inputs of the field grid. Each edits one value of one row; the page keeps the edits until Save.

export function TextInput({ value, onChange, ariaLabel, disabled }) {
  return <input aria-label={ariaLabel} value={value ?? ""} disabled={disabled} onChange={(e) => onChange(e.target.value)} className={inputClass} />;
}

export function NumberInput({ value, onChange, ariaLabel, disabled, min = 0, max = 10000 }) {
  return (
    <input
      type="number"
      aria-label={ariaLabel}
      min={min}
      max={max}
      value={value ?? ""}
      disabled={disabled}
      onChange={(e) => e.target.value !== "" && onChange(Number(e.target.value))}
      className={`${inputClass} w-20`}
    />
  );
}

export function CheckInput({ checked, onChange, ariaLabel, disabled }) {
  return <input type="checkbox" aria-label={ariaLabel} checked={!!checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} className="h-4 w-4 cursor-pointer accent-primary disabled:cursor-not-allowed disabled:opacity-50" />;
}

/** What a field reads: its column path, or the calculation of a computed field. */
export function ReadsCell({ row }) {
  if (!row.compute) return <span className="font-mono text-[11px] text-text_secondary">{row.path || "—"}</span>;
  const summary = computeSummary(row.compute);
  return (
    <span className="inline-flex max-w-[240px] items-center gap-1 text-[11px]" title={summary}>
      <Calculator size={13} className="shrink-0 text-primary" aria-label="Computed" />
      <span className="truncate font-mono text-text_secondary">{summary}</span>
    </span>
  );
}

/** The field's allowed values (enum), with Fill from the data and Clear. */
export function ValuesCell({ values, canFill, busy, editable, onFill, onClear }) {
  const list = values ?? [];
  return (
    <div className="flex items-center gap-1">
      {list.length > 0 ? (
        <Tooltip content={list.join(", ")}>
          <span tabIndex={0} className="cursor-help">
            <Chip tone="brand">{list.length} values</Chip>
          </span>
        </Tooltip>
      ) : (
        <span className="text-xs text-text_muted">—</span>
      )}
      {editable && canFill && (
        <Tooltip content="Fill values from the data">
          <button type="button" aria-label="Fill values from the data" disabled={busy} onClick={onFill} className="inline-flex h-6 w-6 items-center justify-center rounded-md border border-border bg-surface text-text_secondary disabled:opacity-50">
            {busy ? <Loader2 size={12} className="animate-spin" /> : <ListRestart size={12} />}
          </button>
        </Tooltip>
      )}
      {editable && list.length > 0 && (
        <Tooltip content="Clear values">
          <button type="button" aria-label="Clear values" onClick={onClear} className="inline-flex h-6 w-6 items-center justify-center rounded-md border border-border bg-surface text-text_secondary">
            <X size={12} />
          </button>
        </Tooltip>
      )}
    </div>
  );
}
